import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { T } from "@/context/TextEditContext";

const PHONE_KEY = "bk_loyalty_phone";

/**
 * Renders a bright "Track your order →" strip on the homepage the moment a
 * signed-in / previously-checked-out customer has an active order in the
 * kitchen. Invisible if they don't (so it never adds noise for first-time
 * visitors).
 */
export default function ActiveOrderStrip() {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const phone = localStorage.getItem(PHONE_KEY);
    if (!phone) return;
    let cancelled = false;
    const fetchOnce = () => {
      api
        .get(`/orders/by-phone/${phone}`)
        .then((r) => {
          if (!cancelled) {
            const list = r.data || [];
            // Persist per-order track tokens so the order page opens without
            // a re-verify on this device.
            list.forEach((o) => {
              if (o.track_token && o.order_number) {
                localStorage.setItem(`bk_track_${o.order_number}`, o.track_token);
              }
            });
            setOrders(list);
          }
        })
        .catch(() => {});
    };
    fetchOnce();
    // Refresh every 20 seconds so status changes (Received → Cooking → Ready)
    // reflect on the homepage strip without a page refresh.
    const t = setInterval(fetchOnce, 20000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (orders.length === 0) return null;
  const latest = orders[0];
  const isReady = latest.status === "Ready";

  return (
    <Link
      to={`/order/${latest.order_number}`}
      data-testid="active-order-strip"
      className={`mx-4 mt-3 flex items-center gap-3 p-3 rounded-2xl border-2 shadow-md transition-all active:scale-[0.99] ${
        isReady
          ? "bg-emerald-600 border-emerald-800 text-white animate-pulse-slow"
          : "bg-brand-red border-red-900 text-white"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          isReady ? "bg-white text-emerald-700" : "bg-brand-yellow text-brand-red"
        }`}
      >
        <Receipt className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-90">
          {isReady ? (
            <T k="order.strip_ready" d="🎉 Your order is Ready!" />
          ) : (
            <T k="order.strip_active" d="Active Order" />
          )}
        </div>
        <div className="font-display text-xl leading-tight">
          #{latest.order_number}{" "}
          <span className="text-sm opacity-80 font-body normal-case tracking-normal">
            · {latest.status}
          </span>
        </div>
        {orders.length > 1 && (
          <div className="text-[10px] opacity-80 mt-0.5">
            + {orders.length - 1} more active order{orders.length > 2 ? "s" : ""}
          </div>
        )}
      </div>
      <ChevronRight className="w-5 h-5 opacity-80" />
    </Link>
  );
}
